/** The new-match draft (players → teams → rules), kept across reloads until the match is created. */
import { useSyncExternalStore } from 'react';
import { DEFAULT_RULES, upgradeRules, type HouseRules } from '../game/match';
import { NAME_MAX, nameKey, cleanName } from '../shared/constants';
import type { Teams } from '../shared/schemas';
import { KEYS, load, save } from './storage';

/** Colors chosen so white text on them passes WCAG AA (≥ 4.5:1). */
export const TEAM_COLORS = ['#e11d48', '#2563eb', '#b45309', '#7c3aed', '#0e7490', '#be185d', '#c2410c', '#334155'] as const;

export interface DraftPlayer {
  playerId?: string;
  name: string;
  avatarKey: string | null;
}

export interface Draft {
  /** Scorekeeping for a real-life table, or a live online card game. */
  mode?: 'offline' | 'online';
  /** Online only: which game the table plays. */
  variant?: 'open' | 'bandh';
  players: DraftPlayer[];
  /** `seatOrder[seat] = index into players` (seats N, E, S, W). */
  seatOrder: number[];
  teams: Teams;
  rules: HouseRules;
}

export const emptyDraft = (mode: Draft['mode'] = 'offline'): Draft => ({
  mode,
  players: Array.from({ length: 4 }, () => ({ name: '', avatarKey: null })),
  seatOrder: [0, 1, 2, 3],
  teams: { A: { name: 'Team A', color: TEAM_COLORS[0] }, B: { name: 'Team B', color: TEAM_COLORS[1] } },
  rules: DEFAULT_RULES,
});

const saved = load<Draft>(KEYS.draft, emptyDraft());
// Drafts saved by older versions may still carry points-era rules.
let draft: Draft = { ...saved, rules: upgradeRules(saved.rules) };
const listeners = new Set<() => void>();

export function useDraft(): Draft {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => draft,
  );
}

export function updateDraft(fn: (d: Draft) => Draft) {
  draft = fn(draft);
  save(KEYS.draft, draft);
  listeners.forEach((l) => l());
}

export function resetDraft(next: Draft = emptyDraft()) {
  draft = next;
  save(KEYS.draft, next);
  listeners.forEach((l) => l());
}

/** Per-player validation error, or null. */
export function nameErrors(players: DraftPlayer[]): (string | null)[] {
  const keys = players.map((p) => nameKey(p.name));
  return players.map((p, i) => {
    const name = cleanName(p.name);
    if (!name) return 'Name is required';
    // UTF-16 length, matching the server's zod check and the input's maxLength.
    if (name.length > NAME_MAX) return `Max ${NAME_MAX} characters`;
    if (keys.indexOf(keys[i]!) !== i) return 'Name already used';
    return null;
  });
}
